// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import AuthGate from './components/AuthGate';
import MainApp from './components/MainApp';
import LandingPage from './landing-website/LandingPage';
import ThemeProvider from './components/ThemeProvider';
import { CircularProgress, Box } from '@mui/material';
import './App.css';

function App() {
  // CRITICAL: Check URL params FIRST to prevent LandingPage from intercepting
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  const authParam = urlParams.get('auth');
  const token = urlParams.get('token');
  
  // These modes MUST go to AuthGate, never LandingPage
  const hasSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
  const shouldShowAuth = authParam === 'true' || 
                        window.location.pathname === '/auth' || 
                        hasSpecialMode ||
                        (token && (mode === 'invite' || mode === 'reset' || mode === 'confirm'));
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(shouldShowAuth);
  const prevSession = useRef(null);
  
  // If we have special mode, ensure URL path is /auth
  if (hasSpecialMode && window.location.pathname !== '/auth') {
    const newUrl = `/auth${window.location.search}`;
    window.history.replaceState({}, document.title, newUrl);
  }

  const sanitizeCompanyUrl = async () => {
    try {
      const path = window.location.pathname || '';
      if (!path.startsWith('/company/')) return;
      const segments = path.split('/');
      const urlCompanyId = segments[2];
      if (!urlCompanyId) return;

      const { data, error } = await supabase
        .from('companies')
        .select('id')
        .limit(1);

      if (error || !data || data.length === 0) return;
      const authorizedCompanyId = data[0]?.id;
      if (!authorizedCompanyId || authorizedCompanyId === urlCompanyId) return;

      const newPath = ['/company', authorizedCompanyId, ...segments.slice(3)].join('/');
      const newUrl = `${newPath}${window.location.search}${window.location.hash}`;
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    // Check if we should show auth based on URL
    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get('auth');
    const mode = urlParams.get('mode');
    
    // Show auth for: explicit auth param, /auth path, or special modes (invite/reset/confirm)
    if (authParam === 'true' || 
        window.location.pathname === '/auth' ||
        mode === 'invite' ||
        mode === 'reset' ||
        mode === 'confirm') {
      setShowAuth(true);
    }

    // Listen for browser back/forward button
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const isSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
      
      if (window.location.pathname === '/auth' || isSpecialMode) {
        setShowAuth(true);
      } else {
        setShowAuth(false);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Check for an existing session on load
    const fetchSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Check if this is a special mode URL (invite/reset/confirm)
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const isSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
        
        // Verify the session is actually valid
        if (session) {
          const { data: { user }, error } = await supabase.auth.getUser();
          if (error || !user) {
            // Session is invalid, clear it
            await supabase.auth.signOut();
            setSession(null);
            // Only show landing if NOT a special mode
            if (!isSpecialMode) {
              setShowAuth(false);
            }
          } else {
            setSession(session);
            // Don't override showAuth if it's a special mode link
            if (!isSpecialMode) {
              setShowAuth(false); // Don't show auth page if logged in
              // Ensure URL companyId matches authorized company to prevent flash
              await sanitizeCompanyUrl();
            }
          }
        } else {
          setSession(null);
          // Don't override showAuth if special mode or /auth path
          if (!window.location.pathname.includes('/auth') && !isSpecialMode) {
            setShowAuth(false);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        setSession(null);
        // Don't set showAuth to false if it's a special mode
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        if (mode !== 'invite' && mode !== 'reset' && mode !== 'confirm') {
          setShowAuth(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    // Listen for changes in auth state (login, logout, etc)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Check if this is a special mode BEFORE clearing anything
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const isSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
        
        // SECURITY FIX: Clear all cached company data when auth state changes
        if (!session) {
          // User logged out - clear everything (but NOT for invitations!)
          localStorage.removeItem('currentCompanyId');
          localStorage.removeItem('userPermissions');
          
          // DON'T clear invitation data if this is an invitation link!
          if (!isSpecialMode) {
            localStorage.removeItem('pendingInvitationToken');
            localStorage.removeItem('pendingInvitationEmail');
            localStorage.removeItem('pendingInvitationCompany');
            localStorage.removeItem('pendingInvitationRole');
            
            // SECURITY FIX: Clear URL to remove old company ID
            window.history.replaceState({}, document.title, '/');
          }
        } else {
          if (window.location.pathname.startsWith('/company/') && event === 'SIGNED_IN') {
            // Only sanitize URL on actual sign in, don't show loading
            sanitizeCompanyUrl();
          }
        }
        
        // Only update session if it actually changed
        const sessionChanged = (!session && prevSession.current) || 
                             (session && !prevSession.current) ||
                             (session && prevSession.current && session.user?.id !== prevSession.current.user?.id);
        
        if (sessionChanged) {
          setSession(session);
          prevSession.current = session;
        }
        
        // If user logs out, reset to landing page (BUT NOT for special modes!)
        if (!session) {
          // Check if this is a special mode link - DON'T redirect if it is!
          const urlParams = new URLSearchParams(window.location.search);
          const mode = urlParams.get('mode');
          const isSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
          
          if (!isSpecialMode) {
            setShowAuth(false);
            setLoading(false);
            // Clear URL parameters and go to root
            window.history.replaceState({}, document.title, '/');
          } else {
            // Keep showAuth true for special modes
            setShowAuth(true);
            setLoading(false);
          }
        } else {
          // User logged in - hide auth page and show main app (unless special mode)
          const urlParams = new URLSearchParams(window.location.search);
          const mode = urlParams.get('mode');
          const isSpecialMode = mode === 'invite' || mode === 'reset' || mode === 'confirm';
          
          if (!isSpecialMode) {
            setShowAuth(false);
          }
        }
      }
    );

    // Cleanup the subscription on unmount
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (loading) {
    return (
      <ThemeProvider>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            bgcolor: 'background.default'
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  const handleNavigateToAuth = () => {
    setShowAuth(true);
    window.history.pushState({}, document.title, '/auth');
  };

  const handleBackToLanding = () => {
    setShowAuth(false);
    window.history.pushState({}, document.title, '/');
  };

  // CRITICAL CHECK: Never render LandingPage for special modes
  const currentUrlParams = new URLSearchParams(window.location.search);
  const currentMode = currentUrlParams.get('mode');
  const isSpecialModeNow = currentMode === 'invite' || currentMode === 'reset' || currentMode === 'confirm';
  
  return (
    <ThemeProvider>
      {session ? (
        <MainApp user={session.user} />
      ) : (showAuth || isSpecialModeNow) ? (
        <AuthGate onBackToLanding={handleBackToLanding} />
      ) : (
        <LandingPage onNavigateToAuth={handleNavigateToAuth} />
      )}
    </ThemeProvider>
  );
}

export default App;